<template>
  <div class="platform-layout">
    <header class="platform-header">
      <div class="header-brand">
        <div class="header-logo" />
        <span class="header-title">{{ t('layout.productName') }}</span>
      </div>
      <div class="header-right">
        <a-dropdown>
          <a-button class="header-chip">{{ currentBizLabel }}</a-button>
          <template #overlay>
            <a-menu @click="onBizSelect">
              <a-menu-item key="realtime">{{ t('layout.bizRealtimeTxn') }}</a-menu-item>
              <a-menu-item key="compliance">{{ t('layout.bizCompliance') }}</a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
        <a-dropdown>
          <a-button type="text" class="lang-btn">
            <global-outlined />
            {{ currentLanguageLabel }}
          </a-button>
          <template #overlay>
            <a-menu @click="onLanguageSelect">
              <a-menu-item key="zh-CN">{{ t('layout.zhCN') }}</a-menu-item>
              <a-menu-item key="en-US">{{ t('layout.enUS') }}</a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
        <div class="header-divider" />
        <div class="user-avatar" />
        <span class="user-name">{{ t('layout.userName') }}</span>
      </div>
    </header>

    <div class="platform-body">
      <aside class="platform-sidebar" :class="{ collapsed }">
        <div class="sidebar-scroll">
          <div v-for="group in menuGroups" :key="group.id" class="menu-group">
            <div
              class="menu-group-title"
              :class="{ 'has-children': group.children.length > 0, expanded: openGroups[group.id] }"
              :title="collapsed ? t(group.labelKey) : ''"
              @click="toggleGroup(group.id)"
            >
              <span class="menu-text">{{ t(group.labelKey) }}</span>
            </div>
            <transition name="menu-slide">
              <div v-show="openGroups[group.id] && group.children.length > 0" class="menu-children">
                <div
                  v-for="item in group.children"
                  :key="item.key"
                  class="menu-item"
                  :class="{ active: activeMenuKey === item.key }"
                  :title="collapsed ? t(item.labelKey) : ''"
                  @click="openPage(item)"
                >
                  <span v-if="activeMenuKey === item.key" class="menu-dot" />
                  <span class="menu-text">{{ t(item.labelKey) }}</span>
                </div>
              </div>
            </transition>
          </div>
        </div>
        <a-tooltip :title="collapsed ? t('layout.expand') : t('layout.collapse')" placement="right">
          <div class="sidebar-footer" @click="toggleCollapse">
            <MenuFoldOutlined v-if="!collapsed" />
            <MenuUnfoldOutlined v-else />
          </div>
        </a-tooltip>
      </aside>

      <main class="platform-main">
        <div class="platform-tabs">
          <div
            v-for="tab in tabsStore.openTabs"
            :key="tab.key"
            class="platform-tab"
            :class="{ active: tabsStore.activeKey === tab.key }"
            @click="switchTab(tab.key)"
          >
            <span>{{ tabTitle(tab) }}</span>
            <span
              v-if="tabsStore.openTabs.length > 1"
              class="tab-close"
              @click.stop="closeTab(tab.key)"
            >×</span>
          </div>
        </div>
        <div class="platform-content">
          <router-view v-slot="{ Component }">
            <keep-alive>
              <component :is="Component" />
            </keep-alive>
          </router-view>
        </div>
      </main>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'
import {
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons-vue'
import { APP_MENU_GROUPS, ROUTE_MENU_MAP, type AppMenuItem } from '@/config/menu'
import { useLocaleStore } from '@/stores/locale'
import { useTabsStore } from '@/stores/tabs'
import type { AppLocale } from '@/i18n'

const { t } = useI18n()
const localeStore = useLocaleStore()
const tabsStore = useTabsStore()
const route = useRoute()
const router = useRouter()

const collapsed = ref(false)

// 记忆折叠状态
const STORAGE_KEY_COLLAPSE = 'rcs.sidebar.collapsed'
const STORAGE_KEY_GROUPS = 'rcs.sidebar.openGroups'

try {
  collapsed.value = localStorage.getItem(STORAGE_KEY_COLLAPSE) === 'true'
} catch { /* ignore */ }

const menuGroups = APP_MENU_GROUPS

type BizLine = 'realtime' | 'compliance'
const activeBizLine = ref<BizLine>('realtime')

const currentBizLabel = computed(() =>
  activeBizLine.value === 'compliance' ? t('layout.bizCompliance') : t('layout.bizRealtimeTxn')
)

function onBizSelect({ key }: { key: string }) {
  activeBizLine.value = key as BizLine
}

function initOpenGroups(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_GROUPS)
    if (saved) return JSON.parse(saved)
  } catch { /* ignore */ }
  return Object.fromEntries(APP_MENU_GROUPS.map(group => [group.id, !!group.defaultOpen]))
}

const openGroups = reactive<Record<string, boolean>>(initOpenGroups())

function persistOpenGroups(): void {
  try { localStorage.setItem(STORAGE_KEY_GROUPS, JSON.stringify(openGroups)) } catch { /* ignore */ }
}

const activeMenuKey = computed(() => {
  const menuKey = route.meta.menuKey as string | undefined
  return menuKey ?? tabsStore.activeKey
})

const currentLanguageLabel = computed(() =>
  localeStore.locale === 'zh-CN' ? t('layout.zhCN') : t('layout.enUS')
)

function tabTitle(tab: { key: string; title: string }): string {
  const menu = ROUTE_MENU_MAP[tab.key]
  return menu ? t(menu.labelKey) : tab.title
}

function toggleGroup(id: string): void {
  if (collapsed.value) {
    collapsed.value = false
    try { localStorage.setItem(STORAGE_KEY_COLLAPSE, 'false') } catch { /* ignore */ }
  }
  // 互斥展开：打开当前组，关闭其他组
  const willOpen = !openGroups[id]
  if (willOpen) {
    for (const gid of Object.keys(openGroups)) {
      openGroups[gid] = gid === id
    }
  } else {
    openGroups[id] = false
  }
  persistOpenGroups()
}

function toggleCollapse(): void {
  collapsed.value = !collapsed.value
  try { localStorage.setItem(STORAGE_KEY_COLLAPSE, String(collapsed.value)) } catch { /* ignore */ }
}

function openPage(item: AppMenuItem): void {
  tabsStore.openTab(item.key, t(item.labelKey), item.path)
  router.push(item.path)
}

function switchTab(key: string): void {
  const tab = tabsStore.openTabs.find(item => item.key === key)
  if (!tab) return
  tabsStore.switchTab(key)
  router.push(tab.path)
}

function closeTab(key: string): void {
  const closed = tabsStore.closeTab(key)
  if (!closed) {
    message.warning(t('layout.keepOneTab'))
    return
  }
  const active = tabsStore.openTabs.find(item => item.key === tabsStore.activeKey)
  if (active) router.push(active.path)
}

function onLanguageSelect({ key }: { key: string }): void {
  localeStore.setLocale(key as AppLocale)
}

function syncRouteTab(routeName?: string | null, menuKey?: string | null, title?: string, path?: string): void {
  const key = menuKey ?? routeName
  if (!key) return
  const menu = ROUTE_MENU_MAP[key]
  if (!menu) return
  tabsStore.openTab(key, title ?? t(menu.labelKey), path ?? menu.path)
}

watch(
  () => route.fullPath,
  () => {
    const menuKey = (route.meta.menuKey as string) ?? (route.name as string)
    const titleKey = route.meta.titleKey as string | undefined
    syncRouteTab(route.name as string, menuKey, titleKey ? t(titleKey) : undefined, route.fullPath)
  },
  { immediate: true }
)

watch(
  () => localeStore.locale,
  () => {
    tabsStore.openTabs.forEach(tab => {
      const menu = ROUTE_MENU_MAP[tab.key]
      if (menu) tab.title = t(menu.labelKey)
    })
  }
)
</script>

<style lang="less" scoped>
@primary: #165DFF;
@primary-hover: #4080FF;
@primary-light: #E8F0FF;

/* 紫色强调：侧边栏选中 */
@accent: #6C5CE7;
@accent-hover: #7C6FF0;
@accent-light: #F0EDFF;
@accent-bar: #6C5CE7;

@header-bg: #1B2845;
@header-bg-hover: #283556;

@border: #E5E6EB;
@border-split: #F2F3F5;
@text: #1D2129;
@text-secondary: #6B7785;
@bg-page: #F0F2F5;
@bg-card: #FFFFFF;
@bg-sidebar: #F7F8FA;

.platform-layout {
  min-height: 100vh;
  min-width: 1200px;
  background: @bg-page;
}

/* ===== 顶部通栏深蓝色 ===== */
.platform-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  background: @header-bg;
  border-bottom: none;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 24px;
  min-width: 224px;
}

.header-logo {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: linear-gradient(135deg, #6C5CE7 0%, #8B80F5 100%);
  box-shadow: 0 2px 6px rgba(108, 92, 231, 0.4);
  flex-shrink: 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border: 2px solid #fff;
    border-radius: 3px;
  }
}

.header-title {
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 24px;
  margin-left: auto;
}

.header-chip {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
  border-radius: 6px;
  height: 30px;
  padding: 0 14px;
  font-size: 13px;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.35);
    color: #FFFFFF;
  }
}

.lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  border-radius: 6px;

  &:hover {
    color: #FFFFFF;
    background: rgba(255, 255, 255, 0.12);
  }
}

.header-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6C5CE7, #8B80F5);
  border: none;
}

.user-name {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
}

/* ===== 侧边栏 ===== */
.platform-body {
  display: flex;
  min-height: calc(100vh - 56px);
}

.platform-sidebar {
  width: 224px;
  background: @bg-sidebar;
  border-right: 1px solid @border;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  transition: width 0.24s cubic-bezier(0.2, 0, 0, 1);
}

.sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: @border;
    border-radius: 2px;
  }
}

/* 模块分隔线 */
.menu-group + .menu-group {
  border-top: none;
  margin-top: 0;
  padding-top: 0;
}

/* 一级菜单：整行可点击，放大字体 + 左侧留白 */
.menu-group-title {
  display: flex;
  align-items: center;
  padding: 14px 22px 14px 32px;
  font-size: 16px;
  font-weight: 600;
  color: @text;
  cursor: pointer;
  user-select: none;
  transition: background 0.18s ease, color 0.18s ease;

  &:hover {
    background: @accent-light;
    color: @accent;
  }

  /* 无子菜单：透明度弱化 */
  &:not(.has-children) {
    color: @text-secondary;
    font-weight: 500;
    opacity: 0.75;

    &:hover {
      opacity: 1;
    }
  }

  &.has-children {
    opacity: 1;
  }
}

/* 二级菜单：统一缩进 */
.menu-children {
  padding: 2px 0 8px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 22px 9px 52px;
  font-size: 15px;
  color: @text-secondary;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
  border-left: 3px solid transparent;
  position: relative;

  &:hover {
    color: @accent;
    background: @accent-light;
  }

  /* 当前页面：紫条 + 紫字 + 圆点三重高亮 */
  &.active {
    color: @accent;
    background: @accent-light;
    border-left-color: @accent-bar;
    font-weight: 600;
  }
}

/* 圆点高亮指示 */
.menu-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: @accent;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(108, 92, 231, 0.2);
}

.sidebar-footer {
  margin-top: auto;
  text-align: center;
  padding: 12px;
  color: @text-secondary;
  cursor: pointer;
  border-top: 1px solid @border-split;
  font-size: 16px;
  transition: background 0.18s ease, color 0.18s ease;

  &:hover {
    color: @accent;
    background: @accent-light;
  }
}

/* 收起态 */
.platform-sidebar.collapsed {
  width: 60px;

  .menu-text,
  .menu-children {
    display: none;
  }

  .menu-group-title {
    justify-content: center;
    padding: 12px 0;
  }

  .menu-item {
    justify-content: center;
    padding: 10px 0;
  }
}

/* 平滑折叠动画 */
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: max-height 0.24s cubic-bezier(0.2, 0, 0, 1), opacity 0.18s ease;
  overflow: hidden;
  max-height: 600px;
}

.menu-slide-enter-from,
.menu-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/* ===== 主内容区 ===== */
.platform-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: @bg-page;
}

/* 页签栏：白底，激活紫色下划线 */
.platform-tabs {
  display: flex;
  background: @bg-card;
  border-bottom: 1px solid @border;
  padding: 0 16px;
  overflow-x: auto;
  flex-shrink: 0;
  height: 42px;
  align-items: center;

  &::-webkit-scrollbar {
    height: 4px;
  }
}

.platform-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 42px;
  font-size: 13px;
  color: @text-secondary;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition: all 0.2s ease;

  &:hover {
    color: @accent;
  }

  &.active {
    color: @accent;
    border-bottom-color: @accent;
    font-weight: 600;
  }
}

.tab-close {
  color: @text-secondary;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  border-radius: 4px;

  &:hover {
    color: #F53F3F;
    background: #FFF0F0;
  }
}

.platform-content {
  flex: 1;
  overflow: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
